import React from 'react';
import {
  Box,
  Flex,
  HStack,
  Icon,
  LinkBox,
  LinkOverlay,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FiArrowUpRight, FiInbox } from 'react-icons/fi';
import Card from 'components/card/Card';

export function useUnifiedSurfaceStyles() {
  const panelBg = useColorModeValue('rgba(255,255,255,0.92)', 'rgba(13,23,53,0.72)');
  const panelBorder = useColorModeValue('rgba(226, 232, 240, 0.9)', 'rgba(255,255,255,0.14)');
  const mutedText = useColorModeValue('gray.600', 'gray.300');
  const subtleBg = useColorModeValue('blue.50', 'whiteAlpha.100');
  const subtleBorder = useColorModeValue('blue.100', 'whiteAlpha.200');
  return { panelBg, panelBorder, mutedText, subtleBg, subtleBorder };
}

export function useUnifiedInteractiveCardStyles() {
  const hoverBorderColor = useColorModeValue('rgba(148, 163, 184, 0.36)', 'rgba(255,255,255,0.22)');
  const hoverShadow = useColorModeValue(
    '0 24px 56px rgba(9, 30, 66, 0.14)',
    '0 24px 56px rgba(0, 0, 0, 0.32)',
  );

  return {
    transition: 'all 0.24s ease',
    hoverProps: {
      transform: 'translateY(-3px)',
      borderColor: hoverBorderColor,
      boxShadow: hoverShadow,
    },
  };
}

export function useUnifiedButtonStyles() {
  const { subtleBg, subtleBorder } = useUnifiedSurfaceStyles();
  const secondaryColor = useColorModeValue('navy.700', 'white');
  const ghostColor = useColorModeValue('gray.600', 'gray.300');

  return {
    primaryButtonProps: {
      variant: 'brand',
      size: 'lg',
      h: '50px',
      borderRadius: '18px',
      px: 6,
      fontSize: 'md',
      fontWeight: '800',
    },
    secondaryButtonProps: {
      variant: 'outline',
      size: 'md',
      h: '42px',
      borderRadius: '16px',
      px: 4,
      bg: subtleBg,
      borderColor: subtleBorder,
      color: secondaryColor,
      fontWeight: '700',
      _hover: {
        bg: subtleBg,
        borderColor: subtleBorder,
      },
    },
    ghostButtonProps: {
      variant: 'ghost',
      size: 'md',
      h: '42px',
      borderRadius: '16px',
      px: 4,
      color: ghostColor,
      fontWeight: '700',
    },
  };
}

export function UnifiedSurfaceCard({ children, ...props }) {
  const { panelBg, panelBorder } = useUnifiedSurfaceStyles();

  return (
    <Card
      bg={panelBg}
      border="1px solid"
      borderColor={panelBorder}
      borderRadius="20px"
      boxShadow="0 18px 40px rgba(15, 23, 42, 0.08)"
      transition="all 0.25s cubic-bezier(.4, 0, .2, 1)"
      _hover={props._hover || { transform: 'translateY(-2px)', boxShadow: '0 22px 48px rgba(15, 23, 42, 0.12)' }}
      {...props}
    >
      {children}
    </Card>
  );
}

export function UnifiedStatCard({ label, value, helpText, icon = null, accent = 'brand.500', ...props }) {
  const { mutedText, subtleBg } = useUnifiedSurfaceStyles();

  return (
    <UnifiedSurfaceCard p={5} {...props}>
      <HStack justify="space-between" align="center" spacing={4} w="100%">
        <Stat flex="1" minW={0}>
          <StatLabel color={mutedText} fontSize="sm" fontWeight="600" mb={1}>
            {label}
          </StatLabel>
          <StatNumber
            fontSize={{ base: '2xl', md: '3xl' }}
            fontWeight="800"
            color="secondaryGray.900"
            lineHeight="1.2"
          >
            {value}
          </StatNumber>
          {helpText ? (
            <StatHelpText mb={0} color={mutedText} fontSize="xs">
              {helpText}
            </StatHelpText>
          ) : null}
        </Stat>
        {icon ? (
          <Flex
            align="center"
            justify="center"
            w="56px"
            h="56px"
            borderRadius="18px"
            bg={subtleBg}
            color={accent}
            flexShrink={0}
            boxShadow="inner"
          >
            {typeof icon === 'function' ? <Icon as={icon} boxSize={6} /> : icon}
          </Flex>
        ) : null}
      </HStack>
    </UnifiedSurfaceCard>
  );
}

export function UnifiedActionTile({ title, description, to, action = null, icon = FiArrowUpRight }) {
  const { mutedText, subtleBg, subtleBorder } = useUnifiedSurfaceStyles();

  const content = (
    <HStack align="flex-start" justify="space-between" spacing={4}>
      <VStack align="start" spacing={1} flex="1" minW={0}>
        <Text fontWeight="800" color="brand.500" noOfLines={2}>
          {title}
        </Text>
        {description ? (
          <Text fontSize="sm" color={mutedText} noOfLines={3}>
            {description}
          </Text>
        ) : null}
      </VStack>
      <Flex
        align="center"
        justify="center"
        w="38px"
        h="38px"
        borderRadius="14px"
        bg={subtleBg}
        border="1px solid"
        borderColor={subtleBorder}
        color="brand.500"
        flexShrink={0}
      >
        <Icon as={icon} boxSize={6} />
      </Flex>
    </HStack>
  );

  if (to) {
    return (
      <LinkBox
        as={UnifiedSurfaceCard}
        p={4}
        transition="all 0.2s"
        _hover={{ transform: 'translateY(-2px)', boxShadow: '0 18px 34px rgba(15, 23, 42, 0.10)' }}
      >
        <LinkOverlay as={RouterLink} to={to} display="block">
          {content}
        </LinkOverlay>
      </LinkBox>
    );
  }

  return (
    <UnifiedSurfaceCard p={4}>
      {content}
      {action}
    </UnifiedSurfaceCard>
  );
}

export function UnifiedLoadingState({ label = 'Loading…', minH = '160px' }) {
  const { mutedText } = useUnifiedSurfaceStyles();

  return (
    <Flex minH={minH} align="center" justify="center" direction="column" gap={3}>
      <Spinner size="md" color="brand.400" />
      <Text color={mutedText} fontWeight="600">
        {label}
      </Text>
    </Flex>
  );
}

export function UnifiedEmptyState({
  title = 'Nothing here yet',
  description = '',
  minH = '180px',
  icon = FiInbox,
  action = null,
}) {
  const { mutedText, subtleBg, subtleBorder } = useUnifiedSurfaceStyles();

  return (
    <Flex
      minH={minH}
      align="center"
      justify="center"
      direction="column"
      textAlign="center"
      gap={3}
      border="1px dashed"
      borderColor={subtleBorder}
      borderRadius="18px"
      bg={subtleBg}
      px={5}
      py={8}
    >
      <Flex
        align="center"
        justify="center"
        w="52px"
        h="52px"
        borderRadius="18px"
        bg="whiteAlpha.700"
        color="brand.500"
      >
        <Icon as={icon} boxSize={8} />
      </Flex>
      <VStack spacing={1}>
        <Text fontWeight="800">{title}</Text>
        {description ? (
          <Text color={mutedText} maxW="56ch">
            {description}
          </Text>
        ) : null}
      </VStack>
      {action}
    </Flex>
  );
}

export function UnifiedFilterBar({ children, ...props }) {
  const { subtleBg, subtleBorder } = useUnifiedSurfaceStyles();

  return (
    <Flex
      wrap="wrap"
      gap={3}
      p={3}
      borderRadius="18px"
      border="1px solid"
      borderColor={subtleBorder}
      bg={subtleBg}
      {...props}
    >
      {children}
    </Flex>
  );
}

export function UnifiedPageToolbar({ children, ...props }) {
  return (
    <UnifiedSurfaceCard p={{ base: 4, md: 5 }} {...props}>
      <Flex
        direction="column"
        gap={4}
      >
        {children}
      </Flex>
    </UnifiedSurfaceCard>
  );
}

export function UnifiedResultsSection({ title = '', meta = null, actions = null, children, ...props }) {
  const { mutedText } = useUnifiedSurfaceStyles();
  const titleColor = useColorModeValue('navy.800', 'white');

  return (
    <UnifiedSurfaceCard p={{ base: 4, md: 5 }} {...props}>
      {(title || meta || actions) ? (
        <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} gap={3} flexWrap="wrap" mb={4}>
          <VStack align="start" spacing={1}>
            {title ? (
              <Text fontWeight="800" color={titleColor} fontSize={{ base: 'lg', md: 'xl' }}>
                {title}
              </Text>
            ) : null}
            {meta ? (
              typeof meta === 'string' ? (
                <Text color={mutedText} fontSize="sm">
                  {meta}
                </Text>
              ) : (
                meta
              )
            ) : null}
          </VStack>
          {actions}
        </Flex>
      ) : null}
      {children}
    </UnifiedSurfaceCard>
  );
}

export function UnifiedPageSection({ title = '', description = '', action = null, children, ...props }) {
  const { mutedText } = useUnifiedSurfaceStyles();
  const titleColor = useColorModeValue('navy.800', 'white');

  return (
    <UnifiedSurfaceCard p={{ base: 4, md: 5 }} {...props}>
      {(title || description || action) ? (
        <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} gap={3} flexWrap="wrap" mb={4}>
          <VStack align="start" spacing={1}>
            {title ? (
              <Text fontWeight="800" color={titleColor} fontSize={{ base: 'md', md: 'lg' }}>
                {title}
              </Text>
            ) : null}
            {description ? (
              <Text color={mutedText} fontSize="sm">
                {description}
              </Text>
            ) : null}
          </VStack>
          {action}
        </Flex>
      ) : null}
      {children}
    </UnifiedSurfaceCard>
  );
}

export function UnifiedAuthPanel({ children, ...props }) {
  const bg = useColorModeValue('rgba(255,255,255,0.90)', 'rgba(15, 28, 63, 0.72)');
  const borderColor = useColorModeValue('rgba(255,255,255,0.95)', 'rgba(255,255,255,0.16)');

  return (
    <Box
      bg={bg}
      border="1px solid"
      borderColor={borderColor}
      borderRadius={{ base: '24px', md: '28px' }}
      backdropFilter="blur(10px)"
      boxShadow="0 24px 60px rgba(9, 30, 66, 0.16)"
      px={{ base: 5, md: 8 }}
      py={{ base: 6, md: 8 }}
      {...props}
    >
      {children}
    </Box>
  );
}

export function UnifiedDataTableShell({
  loading = false,
  loadingLabel = 'Loading…',
  isEmpty = false,
  emptyTitle = 'No records found',
  emptyDescription = '',
  children,
}) {
  if (loading) {
    return <UnifiedLoadingState label={loadingLabel} />;
  }

  if (isEmpty) {
    return (
      <UnifiedEmptyState
        title={emptyTitle}
        description={emptyDescription}
      />
    );
  }

  return (
    <Box overflowX="auto" borderRadius="18px">
      {children}
    </Box>
  );
}
